import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { trpc } from "@/lib/trpc-client"
import { useRealtimeInvalidate } from "./use-realtime-invalidate"
import type { updateTripsSchema } from "@/db/schema"
import type { z } from "zod"

export const DASHBOARD_KEY = [`trips`, `dashboard`] as const

type DashboardData = Awaited<ReturnType<typeof trpc.trips.dashboard.query>>
type Trip = DashboardData[`trips`][number]

export function useDashboard() {
  const queryClient = useQueryClient()

  // Invalidate whenever trips or days change in Supabase.
  // The subscription only fires "something changed" — no data leak since
  // the refetch goes through authenticated tRPC.
  useRealtimeInvalidate(`trips`, DASHBOARD_KEY)
  useRealtimeInvalidate(`days`, DASHBOARD_KEY)

  const query = useQuery({
    queryKey: DASHBOARD_KEY,
    queryFn: () => trpc.trips.dashboard.query(),
  })

  const deleteTrip = useMutation({
    mutationFn: (id: string) => trpc.trips.delete.mutate({ id }),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: DASHBOARD_KEY })
      const previous = queryClient.getQueryData<DashboardData>(DASHBOARD_KEY)
      queryClient.setQueryData<DashboardData>(DASHBOARD_KEY, (old) =>
        old ? { ...old, trips: old.trips.filter((t) => t.id !== id) } : old
      )
      return { previous }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) queryClient.setQueryData(DASHBOARD_KEY, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY }),
  })

  const deleteTrips = useMutation({
    mutationFn: (ids: string[]) =>
      Promise.all(ids.map((id) => trpc.trips.delete.mutate({ id }))),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: DASHBOARD_KEY })
      const previous = queryClient.getQueryData<DashboardData>(DASHBOARD_KEY)
      const idSet = new Set(ids)
      queryClient.setQueryData<DashboardData>(DASHBOARD_KEY, (old) =>
        old ? { ...old, trips: old.trips.filter((t) => !idSet.has(t.id)) } : old
      )
      return { previous }
    },
    onError: (_err, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(DASHBOARD_KEY, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY }),
  })

  const updateTrip = useMutation({
    mutationFn: ({ id, data }: { id: string; data: z.infer<typeof updateTripsSchema> }) =>
      trpc.trips.update.mutate({ id, data }),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: DASHBOARD_KEY })
      const previous = queryClient.getQueryData<DashboardData>(DASHBOARD_KEY)
      queryClient.setQueryData<DashboardData>(DASHBOARD_KEY, (old) => {
        if (!old) return old
        return { ...old, trips: old.trips.map((t) => (t.id === id ? { ...t, ...data } as Trip : t)) }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(DASHBOARD_KEY, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: DASHBOARD_KEY }),
  })

  return {
    trips: (query.data?.trips ?? []) as Trip[],
    days: query.data?.days ?? [],
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    deleteTrip,
    deleteTrips,
    updateTrip,
  }
}
